import { useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { UseAgentUpdate, useAgent } from '@copilotkit/react-core/v2';
import { MyAuth0Context } from 'index';
import { AGENT_ATTACHMENTS_URL, VANTAGE_AGENT_ID } from 'shared/constants';
import type { AttachmentRef, ChatError } from '../types';
import type { ChatRuntime, ChatRuntimeSnapshot, SendInput, UseChatRuntime } from './types';
import { toAgUiSnapshot } from './toAgUiSnapshot';

// Local structural mirrors of the AG-UI surface this adapter actually touches.
// Two copies of `@ag-ui/client` end up under node_modules (one direct, one
// transitive via `@copilotkit/runtime-client-gql`). Importing the official
// types triggers a "Type X from path A is not assignable to type X from path
// B" mismatch even though the runtime objects are interchangeable. Defining
// the surface locally pins us to the shape we depend on without taking sides
// on which copy "wins" — and it keeps this file resilient to future minor
// upgrades that nudge field types without changing the wire shape.
interface LocalSubscriber {
  onMessagesChanged?: (params: unknown) => void;
  onStateChanged?: (params: unknown) => void;
  onRunInitialized?: (params: { input?: { runId?: string } }) => void;
  onRunFinalized?: (params: unknown) => void;
  onRunFailed?: (params: { error: Error }) => void;
  onTextMessageStartEvent?: (params: unknown) => void;
}
interface LocalAgent {
  messages: ReadonlyArray<unknown>;
  state: unknown;
  headers?: Record<string, string>;
  url?: string;
  subscribe(sub: LocalSubscriber): { unsubscribe: () => void };
  addMessage(message: unknown): void;
  runAgent(parameters?: { forwardedProps?: Record<string, unknown> }): Promise<unknown>;
  abortRun(): void;
}

interface RunLifecycleState {
  phase: 'idle' | 'thinking' | 'streaming';
  runId: string | null;
  lastError: ChatError | null;
  messagesVersion: number;
}

const INITIAL_LIFECYCLE: RunLifecycleState = {
  phase: 'idle',
  runId: null,
  lastError: null,
  messagesVersion: 0
};

type LifecycleAction =
  | { type: 'started'; runId: string | null }
  | { type: 'textMessageStarted' }
  | { type: 'finalized' }
  | { type: 'failed'; error: ChatError }
  | { type: 'messagesChanged' }
  | { type: 'stateChanged' };

const lifecycleReducer = (state: RunLifecycleState, action: LifecycleAction): RunLifecycleState => {
  switch (action.type) {
    case 'started':
      return { phase: 'thinking', runId: action.runId, lastError: null, messagesVersion: state.messagesVersion };
    case 'textMessageStarted':
      // No-op when idle — out-of-order event, defensive.
      if (state.phase === 'idle') return state;
      return { ...state, phase: 'streaming' };
    case 'finalized':
      return { ...state, phase: 'idle' };
    case 'failed':
      return { phase: 'idle', runId: state.runId, lastError: action.error, messagesVersion: state.messagesVersion };
    case 'messagesChanged':
    case 'stateChanged':
      return { ...state, messagesVersion: state.messagesVersion + 1 };
    default:
      return state;
  }
};

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * Production runtime adapter for Phase 2. Wraps `useAgent` from CopilotKit v2,
 * subscribes to AG-UI run lifecycle events to derive `isStreaming`/`runId`/
 * `lastError`, and projects `agent.messages` + `agent.state` into the Vantage
 * `ChatRuntimeSnapshot` via the pure `toAgUiSnapshot` function.
 *
 * Lives behind the `ChatRuntimeContext` provider mounted inside `CopilotKitMount`,
 * so anything reading `useChatRuntime()` from the chat drawer's subtree resolves
 * to this hook in production while tests continue to fall back to the Phase 1
 * `useMockRuntime`.
 * @returns the live `ChatRuntime` for the current React subtree.
 */
export const useCopilotKitRuntime: UseChatRuntime = (): ChatRuntime => {
  const { agent: rawAgent } = useAgent({
    agentId: VANTAGE_AGENT_ID,
    updates: [UseAgentUpdate.OnMessagesChanged, UseAgentUpdate.OnStateChanged, UseAgentUpdate.OnRunStatusChanged]
  });
  const agent = rawAgent as unknown as LocalAgent;
  const { getAccessTokenSilently } = useContext(MyAuth0Context);

  const [lifecycle, dispatch] = useReducer(lifecycleReducer, INITIAL_LIFECYCLE);

  // AG-UI exposes lifecycle via three subscription callbacks (no `runStatus`
  // field). Subscribe once per agent and dispatch into the local reducer so
  // `isStreaming` flips around the active run rather than chasing field
  // changes that don't exist on the agent itself.
  useEffect(() => {
    const subscriber: LocalSubscriber = {
      onRunInitialized: ({ input }) => {
        dispatch({ type: 'started', runId: input?.runId ?? null });
      },
      onRunFinalized: () => {
        dispatch({ type: 'finalized' });
      },
      onRunFailed: ({ error }) => {
        dispatch({
          type: 'failed',
          error: { message: error?.message ?? 'Agent run failed.', retryable: true }
        });
      },
      onTextMessageStartEvent: () => dispatch({ type: 'textMessageStarted' }),
      onMessagesChanged: () => dispatch({ type: 'messagesChanged' }),
      onStateChanged: () => dispatch({ type: 'stateChanged' })
    };
    const subscription = agent.subscribe(subscriber);
    return () => subscription.unsubscribe();
  }, [agent]);

  const snapshot = useMemo<ChatRuntimeSnapshot>(
    () =>
      toAgUiSnapshot(
        agent.messages as Parameters<typeof toAgUiSnapshot>[0],
        lifecycle.phase,
        lifecycle.runId,
        lifecycle.lastError,
        agent.state
      ),
    [agent.messages, agent.state, lifecycle.phase, lifecycle.runId, lifecycle.lastError, lifecycle.messagesVersion]
  );

  // Track the most recent send so `retry()` can replay the same idempotency
  // key + page context without forcing the caller to thread them back in.
  const lastSendRef = useRef<SendInput | null>(null);

  const send = useCallback(
    async (input: SendInput): Promise<void> => {
      lastSendRef.current = input;
      const userMessage = { id: newId(), role: 'user' as const, content: input.text };
      agent.addMessage(userMessage);
      // `attachments` and `pageContext` ride along on `forwardedProps`; the
      // backend re-derives clientId from JWT and validates IDs server-side
      // (see plan §5). Idempotency key passed through forwardedProps too so
      // the stub can echo it back; production will lift it into a header.
      await runWithAuth(
        agent,
        getAccessTokenSilently,
        {
          attachments: input.attachments,
          pageContext: input.pageContext,
          idempotencyKey: idempotencyKeyFor(userMessage.id)
        },
        input.profileId
      );
    },
    [agent, getAccessTokenSilently]
  );

  const cancel = useCallback(() => {
    // AG-UI cancellation primitive verified at
    // node_modules/@ag-ui/client/dist/index.d.ts:491 (AbstractAgent.abortRun)
    // and HttpAgent.abortRun() at line 366.
    agent.abortRun();
  }, [agent]);

  const retry = useCallback(
    async (messageId: string): Promise<void> => {
      const previous = lastSendRef.current;
      if (!previous) return;
      await runWithAuth(
        agent,
        getAccessTokenSilently,
        {
          attachments: previous.attachments,
          pageContext: previous.pageContext,
          idempotencyKey: idempotencyKeyFor(messageId),
          retryOf: messageId
        },
        previous.profileId
      );
    },
    [agent, getAccessTokenSilently]
  );

  const uploadAttachment = useCallback(
    async (file: File, signal: AbortSignal, options?: { profileId?: number | null }): Promise<AttachmentRef> => {
      const token = await getAccessTokenSilently();
      // Backend's `POST /agent/attachments` uses `FilesInterceptor('files', 5)`
      // and returns `AttachmentResponseDto[]` so a single multipart shape can
      // serve both single and batched uploads. The session hook calls this
      // function once per file, so we read the first element of the array.
      const body = new FormData();
      body.append('files', file);
      const uploadUrl = new URL(AGENT_ATTACHMENTS_URL);
      const { profileId } = options ?? {};
      if (profileId !== undefined && profileId !== null) {
        uploadUrl.searchParams.set('profileId', String(profileId));
      }
      const res = await fetch(uploadUrl.toString(), {
        method: 'POST',
        body,
        signal,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) {
        throw new Error(`Attachment upload failed (${res.status})`);
      }
      const parsed = (await res.json()) as
        | Array<{ ref: string; mimeType: string; sizeBytes: number }>
        | { ref: string; mimeType: string; sizeBytes: number };
      const entry = Array.isArray(parsed) ? parsed[0] : parsed;
      if (!entry || !entry.ref) {
        throw new Error('Attachment upload returned no ref');
      }
      return {
        ref: entry.ref,
        name: file.name,
        sizeBytes: entry.sizeBytes ?? file.size,
        mimeType: entry.mimeType ?? file.type ?? 'application/octet-stream'
      };
    },
    [getAccessTokenSilently]
  );

  return useMemo<ChatRuntime>(
    () => ({ snapshot, send, cancel, retry, uploadAttachment }),
    [snapshot, send, cancel, retry, uploadAttachment]
  );
};

const idempotencyKeyFor = (id: string) => `vantage-${id}`;

interface RunForwardedProps {
  attachments: AttachmentRef[];
  pageContext: SendInput['pageContext'];
  idempotencyKey: string;
  retryOf?: string;
}

/**
 * Trigger an AG-UI run after refreshing the agent's bearer token from Auth0
 * and applying the per-send `profileId` query parameter to the gateway URL.
 * Both mutations happen at the same lifecycle moment (immediately before
 * `agent.runAgent()`) so they stay in sync across retries and token rotations.
 * @param agent - The AG-UI agent to run.
 * @param getAccessTokenSilently - Auth0 token accessor.
 * @param forwardedProps - Side-channel data the backend correlates with the run.
 * @param profileId - Optional impersonation profile. Applied as `?profileId=<n>`
 *   on the gateway URL only — not propagated in forwardedProps or headers.
 */
const runWithAuth = async (
  agent: LocalAgent,
  getAccessTokenSilently: () => Promise<string>,
  forwardedProps: RunForwardedProps,
  profileId?: number | null
): Promise<void> => {
  const token = await getAccessTokenSilently().catch(() => '');
  if (token && agent.headers) {
    agent.headers = { ...agent.headers, Authorization: `Bearer ${token}` };
  }
  // Idempotent per-send URL mutation: delete then optionally re-set so
  // consecutive sends and retries never accumulate duplicate query params.
  if (agent.url !== undefined) {
    const baseUrl = new URL(agent.url);
    baseUrl.searchParams.delete('profileId');
    if (profileId !== undefined && profileId !== null) {
      baseUrl.searchParams.set('profileId', String(profileId));
    }
    agent.url = baseUrl.toString();
  }
  await agent.runAgent({
    forwardedProps: forwardedProps as unknown as Record<string, unknown>
  });
};
